<script setup lang="ts">
/**
 * The `<head>` a crawler sees: title, description, Open Graph, Twitter card.
 *
 * ── Text only, here ──────────────────────────────────────────────────────────
 *
 * The backend also serves icon and share-image *uploads*, and those are
 * deliberately not in this pane. An upload needs a file picker, a preview, the
 * per-slot dimension rules (`ICON_SLOTS` — 16, 32, 100, 180, 240 and an .ico)
 * and the errors the byte-parsing validator returns, which is a screen of its
 * own rather than a row in this form. The endpoints are untouched and still
 * take uploads; what is missing is a UI for them, and saying so is better than
 * a half-built picker that silently rejects.
 *
 * The keys are the API's own, **unprefixed** — `title`, not `meta.title`. The
 * `meta.` prefix is how they are stored in `game_settings`; sending a prefixed
 * key is rejected as unknown.
 *
 * `image_width` / `image_height` are omitted for a stronger reason: the API
 * **rejects** them on this endpoint. They are derived by the upload
 * from the file's own header, because a pasted URL has no size anyone can
 * trust.
 */
import { ref, watch } from 'vue';

import { adminApi } from './admin-api';
import { usePane } from './pane';

const meta = usePane(() => adminApi.meta());
const draft = ref<Record<string, string>>({});

watch(meta.data, (v) => (draft.value = { ...((v?.values ?? {}) as Record<string, string>) }), {
  immediate: true,
});

const FIELDS: ReadonlyArray<{ key: string; label: string; hint?: string }> = [
  { key: 'title', label: 'Title', hint: 'falls back to BRAND_NAME' },
  {
    key: 'description',
    label: 'Description',
    hint: 'used for both Open Graph and Twitter Card previews — keep under ~200 characters',
  },
  { key: 'site_name', label: 'Site name', hint: 'falls back to the title' },
  { key: 'url', label: 'Canonical URL', hint: 'falls back to APP_URL' },
  { key: 'locale', label: 'Locale', hint: 'e.g. en_US' },
  { key: 'image', label: 'Share image URL' },
  { key: 'image_alt', label: 'Share image alt text' },
  { key: 'twitter_card', label: 'Twitter card', hint: 'summary_large_image' },
];

const saved = ref(false);
</script>

<template>
  <h1>Meta tags</h1>
  <p class="lede">
    What a link to this game looks like when somebody pastes it into a chat.
  </p>

  <p v-if="meta.error.value" class="bad">{{ meta.error.value }}</p>
  <p v-if="saved" class="note">Saved.</p>

  <section>
    <h2>Tags</h2>
    <div class="fields">
      <label v-for="f in FIELDS" :key="f.key" class="field">
        <span>{{ f.label }}<template v-if="f.hint"> · {{ f.hint }}</template></span>
        <input v-model="draft[f.key]" type="text" :data-test="`admin-${f.key}`" />
      </label>
    </div>
    <div class="toolbar" style="margin-top: 0.6rem">
      <button
        class="accent"
        :disabled="meta.loading.value"
        data-test="admin-save-meta"
        @click="meta.act(() => adminApi.setMeta(draft)).then(() => (saved = true))"
      >
        Save
      </button>
    </div>
    <p class="note">
      Icons and the share image are uploaded, not typed, and have no screen here
      yet — the endpoints exist (<code>POST /api/admin/meta/icons/:slot</code>)
      but need a file picker with the per-slot size rules behind it.
    </p>
  </section>
</template>
